<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import { token } from './api'
import { admin, can } from './main'
import type { Permission } from './api'
import { t } from './i18n'
import LangSwitch from './components/LangSwitch.vue'
import EnvBanner from './components/EnvBanner.vue'
import BrandSwitch from './components/BrandSwitch.vue'
import { brandView } from './brand'

/**
 * The shell: a white TOP BAR over the #f7f8fa canvas, content full width below.
 *
 * 🔄 2026-10-14 (operator): the 280px KNE sidebar became a horizontal bar, to
 * give the tables — the customer directory, transfers, sweeps — the width the
 * rail took. KNE's active-item signature carries over turned on its side: a
 * 3px BOTTOM border in the brand blue on a pale blue ground, readable at a
 * glance without a filled pill. The two groups (money · people) keep their
 * order and are split by a hairline; their names live on as `title`s.
 * Referrals left the bar 2026-10-14 — they are a tab of Clients now.
 *
 * Below `xl` the items do not fit, so a menu button opens them as a panel
 * under the bar — the same list, grouped and labelled.
 *
 * The page frame lives here, so a view cannot ship without it. That was a real
 * bug: Treasury ran full-bleed while every other page sat in a column.
 */
const route = useRoute()
const router = useRouter()
const open = ref(false)

// Navigating closes the small-screen panel.
watch(() => route.fullPath, () => (open.value = false))

function signOut() {
  token.clear()
  admin.value = null
  void router.push({ name: 'sign-in' })
}

// `brand` shows an entry only in that brand's view: Prospects are Tothor's
// alone, never Global or 1cards (operator, 2026-11-01).
type NavItem = { to: string; label: string; icon: string; perm?: Permission; brand?: 'tothor' }

// `perm` hides an entry whose screen would only answer 403 for this role
// (SUPPORT has no `finance:read`; Team is SUPER_ADMIN only) — 2026-10-14.
const ALL_NAV: { group: string; items: NavItem[] }[] = [
  {
    group: 'nav.groupMoney',
    items: [
      { to: 'pipeline', label: 'nav.pipeline', icon: '⇄', perm: 'finance:read' },
      { to: 'home', label: 'nav.home', icon: '◧' },
      { to: 'treasury', label: 'nav.treasury', icon: '◈', perm: 'finance:read' },
      { to: 'transfers', label: 'nav.transfers', icon: '⇆', perm: 'finance:read' },
    ],
  },
  {
    group: 'nav.groupPeople',
    items: [
      { to: 'customers', label: 'nav.customers', icon: '◍' },
      { to: 'team', label: 'nav.team', icon: '◎', perm: 'admins:write' },
    ],
  },
  {
    group: 'nav.groupSales',
    items: [{ to: 'prospects', label: 'nav.prospects', icon: '◔', perm: 'prospects:read', brand: 'tothor' }],
  },
]

const NAV = computed(() =>
  ALL_NAV.map((g) => ({
    ...g,
    items: g.items.filter((i) => (!i.perm || can(i.perm)) && (!i.brand || i.brand === brandView.value)),
  })).filter(
    (g) => g.items.length,
  ),
)

const SECTIONS: Record<string, string[]> = {
  customers: ['customers', 'customer'],
  team: ['team', 'operator'],
  prospects: ['prospects', 'prospect'],
}
const active = (name: string) => (SECTIONS[name] ?? [name]).includes(String(route.name ?? ''))
</script>

<template>
  <RouterView v-if="route.meta.public" />

  <div v-else class="flex h-dvh flex-col bg-ink">
    <!-- Top bar: white, hairline bottom edge -->
    <!-- z-[60]: above EnvBanner (sticky z-50), or the strip covers the open small-screen menu --><header class="relative z-[60] flex-none border-b border-line bg-surface">
      <div class="flex h-14 items-stretch gap-6 px-6 max-xl:gap-4 max-lg:px-4">
        <RouterLink :to="{ name: 'home' }" class="flex flex-none items-center gap-2.5">
          <span class="page-icon !h-8 !w-8 !rounded-lg !text-sm">T</span>
          <span class="text-base font-bold tracking-tight">
            tothor<span class="text-brand">/ops</span>
          </span>
        </RouterLink>

        <!-- The menu, horizontal from `xl` up. It was `lg` until 2026-11-01, when
             Prospects made a seventh entry: at 1024 the bar already clipped
             Team behind the brand switch (measured in a screenshot). -->
        <nav class="flex min-w-0 items-stretch max-xl:hidden" :aria-label="t('app.menu')">
          <template v-for="(g, gi) in NAV" :key="g.group">
            <span v-if="gi" class="mx-2 my-4 w-px flex-none bg-line" aria-hidden="true"></span>
            <div class="flex items-stretch" role="group" :title="t(g.group)" :aria-label="t(g.group)">
              <RouterLink
                v-for="i in g.items"
                :key="i.to"
                :to="{ name: i.to }"
                class="flex items-center gap-2 whitespace-nowrap border-b-[3px] px-3 pt-[3px] text-[0.875rem] font-medium transition-colors max-xl:px-2.5"
                :class="
                  active(i.to)
                    ? 'border-brand bg-brand-light font-semibold text-brand'
                    : 'border-transparent text-fg-dim hover:bg-[#f8fafc] hover:text-brand'
                "
                :aria-current="active(i.to) ? 'page' : undefined"
              >
                <!-- Icons only from `2xl`: below 1536 the seven labels need
                     their width (measured 2026-11-01 in French, at 1440). -->
                <span class="w-4 text-center opacity-80 max-2xl:hidden" aria-hidden="true">{{ i.icon }}</span>
                {{ t(i.label) }}
              </RouterLink>
            </div>
          </template>
        </nav>

        <div class="ml-auto flex flex-none items-center gap-3">
          <!-- Below `sm` the bar has no room; the switch moves into the menu panel. -->
          <BrandSwitch class="max-sm:hidden" />
          <LangSwitch />
          <!-- Between `xl` and `2xl` the email would push the menu under the
               brand switch: the account link shrinks to an initial. -->
          <RouterLink
            :to="{ name: 'account' }"
            class="hidden h-8 w-8 place-items-center rounded-full border border-line text-[13px] font-semibold uppercase text-fg-dim hover:border-brand hover:text-brand xl:grid 2xl:hidden"
            :class="route.name === 'account' && 'border-brand text-brand'"
            :title="`${t('nav.account')} · ${admin?.email ?? ''}`"
          >{{ (admin?.first_name || admin?.email || '?').slice(0, 1) }}</RouterLink>
          <RouterLink
            :to="{ name: 'account' }"
            class="max-w-[16rem] truncate text-[0.8rem] text-fg-dim hover:text-brand max-2xl:hidden"
            :class="route.name === 'account' && 'font-semibold text-brand'"
            :title="t('nav.account')"
          >{{ admin?.email }}</RouterLink>
          <button type="button" class="btn btn-xs max-xl:hidden" @click="signOut">{{ t('app.signOut') }}</button>
          <button
            type="button"
            class="grid h-8 w-8 place-items-center rounded-lg border border-line text-fg-dim hover:bg-[#f8fafc] hover:text-brand xl:hidden"
            :aria-label="t('app.menu')"
            :aria-expanded="open"
            @click="open = !open"
          >
            {{ open ? '✕' : '☰' }}
          </button>
        </div>
      </div>

      <!-- Below `xl`: the same menu as a panel under the bar -->
      <div
        v-if="open"
        class="absolute inset-x-0 top-full border-b border-line bg-surface pb-3 shadow-sm xl:hidden"
      >
        <template v-for="g in NAV" :key="g.group">
          <div class="px-5 pb-1 pt-3 text-[0.7rem] font-semibold uppercase tracking-[0.05em] text-fg-faint">
            {{ t(g.group) }}
          </div>
          <RouterLink
            v-for="i in g.items"
            :key="i.to"
            :to="{ name: i.to }"
            class="flex items-center gap-3 border-l-[3px] px-5 py-2.5 text-[0.9rem] font-medium"
            :class="
              active(i.to)
                ? 'border-brand bg-brand-light font-semibold text-brand'
                : 'border-transparent text-fg-dim hover:bg-[#f8fafc] hover:text-brand'
            "
          >
            <span class="w-4 text-center opacity-80" aria-hidden="true">{{ i.icon }}</span>
            {{ t(i.label) }}
          </RouterLink>
        </template>
        <div class="mt-2 border-t border-line px-5 pt-3 sm:hidden">
          <BrandSwitch />
        </div>
        <div class="mt-2 flex items-center gap-3 border-t border-line px-5 pt-3">
          <RouterLink :to="{ name: 'account' }" class="min-w-0 flex-1 truncate text-[0.8rem] text-fg-dim hover:text-brand">
            {{ t('nav.account') }} · {{ admin?.email }}
          </RouterLink>
          <button type="button" class="btn btn-xs" @click="signOut">{{ t('app.signOut') }}</button>
        </div>
      </div>
    </header>

    <EnvBanner />

    <!-- Scrim for the small-screen panel -->
    <div v-if="open" class="fixed inset-0 z-30 bg-black/20 xl:hidden" @click="open = false"></div>

    <main class="min-h-0 flex-1 overflow-y-auto px-8 pb-8 pt-5 max-lg:px-4">
      <!-- Keyed on the brand view: switching re-mounts the page, so every
           screen refetches through the new X-Brand without watching it. -->
      <RouterView v-slot="{ Component }">
        <component :is="Component" :key="brandView" />
      </RouterView>
    </main>
  </div>
</template>
